/**
 * @fileOverview Loads city data and passes it to child component.
 * Loads the following info and passes down as props
 * - brandAmbassadors
 */

import React, { useState, useEffect } from 'react'
import { getCityUsers } from './../../../Module/graphQL'
import {
  STATUS_BLOCKED,
  BRAND_AMBASSADOR_STATUS_ENUM,
  ROLE_ADMIN,
  ROLE_CITY_MANAGER,
  ROLE_SPY,
  ROLE_DISTRIBUTOR,
  BRAND_AMBASSADORS_STATUS_TYPE_TO_ENUM,
} from '../../../shared/constants'
const validateResponse = res => {
  if (res['errorMessage']) {
    throw new Error(res['errorMessage'])
  }
  if (res.errors && res.errors[0]) {
    throw new Error(res.errors[0].message)
  }
}

const getCityUsersCombined = async (
  cityId,
  queryUsers = 'brandAmbassadors',
) => {
  const [
    activeUsers,
    blockedWithInventoryUsers,
    blockedWithMissionsUsers,
  ] = await Promise.all([
    (await getCityUsers(
      {
        cityId,
        statuses: [BRAND_AMBASSADOR_STATUS_ENUM.ACTIVE],
      },
      queryUsers,
    )).json(),
    (await getCityUsers(
      {
        cityId,
        hasInventory: true,
        statuses: [BRAND_AMBASSADOR_STATUS_ENUM.BLOCKED],
      },
      queryUsers,
    )).json(),
    (await getCityUsers(
      {
        cityId,
        hasMissionsBooked: true,
        statuses: [BRAND_AMBASSADOR_STATUS_ENUM.BLOCKED],
      },
      queryUsers,
    )).json(),
  ])
  validateResponse(activeUsers)
  validateResponse(blockedWithInventoryUsers)
  validateResponse(blockedWithMissionsUsers)

  return [
    ...activeUsers.data.city[queryUsers].edges,
    ...blockedWithInventoryUsers.data.city[queryUsers].edges,
    ...blockedWithMissionsUsers.data.city[queryUsers].edges,
  ]
}

const BrandAmbassadorData = props => {
  const { children, cityId, ...otherProps } = props

  const [data, setData] = useState({
    brandAmbassadors: undefined,
  })

  // Will be set to true once the filter info has been requested
  const [requestedFilterInfo, setRequestedFilterInfo] = useState(false)

  // This fetches all the data we need for the global planning filters. For
  // example we need to know all the campaigns and clients that operate in the
  // current city
  useEffect(() => {
    if (requestedFilterInfo) return
    setRequestedFilterInfo(true)
    ;(async () => {
      const brandAmbassadorsRaw = await getCityUsersCombined(cityId)
      const adminsRaw = await getCityUsersCombined(cityId, 'admins')
      const cityManagersRaw = await getCityUsersCombined(cityId, 'cityManagers')
      const spiesRaw = await getCityUsersCombined(cityId, 'spyList')

      setData({
        brandAmbassadors: [
          ...brandAmbassadorsRaw.map(({ node }) => ({
            id: node.id,
            name: node.firstName + ' ' + node.lastName,
            flag: node.status == STATUS_BLOCKED,
            userRole: ROLE_DISTRIBUTOR,
            status: BRAND_AMBASSADORS_STATUS_TYPE_TO_ENUM[node.status],
          })),
          ...adminsRaw.map(({ node }) => ({
            id: node.id,
            name: node.firstName + ' ' + node.lastName,
            flag: node.status == STATUS_BLOCKED,
            userRole: ROLE_ADMIN,
            status: BRAND_AMBASSADORS_STATUS_TYPE_TO_ENUM[node.status],
          })),
          ...cityManagersRaw.map(({ node }) => ({
            id: node.id,
            name: node.firstName + ' ' + node.lastName,
            flag: node.status == STATUS_BLOCKED,
            userRole: ROLE_CITY_MANAGER,
            status: BRAND_AMBASSADORS_STATUS_TYPE_TO_ENUM[node.status],
          })),
          ...spiesRaw.map(({ node }) => ({
            id: node.id,
            name: node.firstName + ' ' + node.lastName,
            flag: node.status == STATUS_BLOCKED,
            userRole: ROLE_SPY,
            status: BRAND_AMBASSADORS_STATUS_TYPE_TO_ENUM[node.status],
          })),
        ],
      })
    })()
  })

  return React.cloneElement(children, {
    ...otherProps,
    cityId,
    ...data,
  })
}

export default BrandAmbassadorData
